import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Guide, Step, StepImage } from './types';
import { guideHasConclusion } from './types';

// postMessage bridge between the v5 embed and the frame that hosts it (FixBot's
// iframe drawer). The message `source`/`v`/shapes are a frozen contract.
//
//   embed -> host:  { type: 'ready', guideid }
//                   { type: 'stepChange', guideid, stepNumber, stepTitle, stepText,
//                                         imageIndex, imageUrl, imageCount }
//                   { type: 'imageChange', guideid, stepNumber,
//                                          imageIndex, imageUrl, imageCount }
//   host  -> embed: { type: 'goToStep', stepNumber }
//
// Every message carries { source, v } and is origin-validated. The host names
// its origin via `?parentOrigin=`; with no valid parent the bridge stays inert
// (never posts to '*', ignores inbound messages).

const SOURCE = 'ifixit-guide-embed';
const PROTOCOL_VERSION = 1;

// stepNumber 0 is the intro, N+1 the conclusion, -1 the title — none map to a
// step. Cap shared step text so a long step can't bloat the message.
const STEP_TEXT_MAX_LENGTH = 1500;

// Origins allowed to host the embed and exchange messages with it. Production
// hosts are exact names so an unrelated preview or dev site cannot receive
// guide data from an authenticated embed.
// Per-PR Vercel aliases are intentionally excluded: hostnames alone do not
// establish iFixit ownership. Preview integration uses fixbot-preview.ifixit.com.
const ALLOWED_ORIGIN_PATTERNS = [
   /^https:\/\/fixbot\.ifixit\.com$/,
   /^https:\/\/fixbot-preview\.ifixit\.com$/,
   /^http:\/\/localhost(:\d+)?$/,
   /^http:\/\/127\.0\.0\.1(:\d+)?$/,
];

function isAllowedOrigin(origin: string | null): origin is string {
   return typeof origin === 'string' && ALLOWED_ORIGIN_PATTERNS.some(p => p.test(origin));
}

function resolveParentOrigin(): string | null {
   const requested = new URLSearchParams(window.location.search).get('parentOrigin');
   return requested && isAllowedOrigin(requested) ? requested : null;
}

interface ImageInfo {
   imageIndex: number | null;
   imageUrl: string | null;
   imageCount: number;
}

interface Params {
   guide: Guide;
   // 0 = intro, 1..N = steps, N+1 = conclusion, -1 = title screen.
   stepNumber: number;
   onGoToStep: (stepNumber: number) => void;
}

/**
 * Wire the embed to its host frame. Posts `ready` on mount, `stepChange` when the
 * visible step changes, and exposes `notifyImageChange` for the gallery to call
 * on scrub; handles inbound `goToStep`. Inert when no trusted parent origin is
 * present. All live values are held in refs so the once-attached listener and
 * stable callbacks read current state without rebinding.
 */
export function useParentBridge({ guide, stepNumber, onGoToStep }: Params) {
   const targetOrigin = useMemo(() => resolveParentOrigin(), []);
   const guideRef = useRef(guide);
   const onGoToStepRef = useRef(onGoToStep);
   // Last image reported (step + index) so an imageChange landing on the image a
   // stepChange just announced doesn't post a redundant message.
   const lastImageKeyRef = useRef<string | null>(null);
   useEffect(() => {
      guideRef.current = guide;
      onGoToStepRef.current = onGoToStep;
   });

   const post = useCallback(
      (message: Record<string, unknown>) => {
         if (!targetOrigin) {
            return;
         }
         window.parent.postMessage(
            { source: SOURCE, v: PROTOCOL_VERSION, ...message },
            targetOrigin
         );
      },
      [targetOrigin]
   );

   const goToStep = useCallback((requested: unknown) => {
      if (!Number.isInteger(requested)) {
         return;
      }
      const step = requested as number;
      const guide = guideRef.current;
      const stepCount = guide.steps.length;
      const maxIndex = stepCount + (guideHasConclusion(guide) ? 1 : 0);
      if (step < 0 || step > maxIndex) {
         return;
      }
      onGoToStepRef.current(step);
   }, []);

   // Attach the inbound listener, then announce readiness last so a host that
   // fires a command in response can't race the listener.
   useEffect(() => {
      if (!targetOrigin) {
         return;
      }
      const onMessage = (event: MessageEvent) => {
         if (event.origin !== targetOrigin) {
            return;
         }
         const data = event.data;
         if (!data || data.source !== SOURCE || data.v !== PROTOCOL_VERSION) {
            return;
         }
         if (data.type === 'goToStep') {
            goToStep(data.stepNumber);
         }
      };
      window.addEventListener('message', onMessage);
      post({ type: 'ready', guideid: guideRef.current.guideid });
      return () => window.removeEventListener('message', onMessage);
   }, [targetOrigin, post, goToStep]);

   // stepChange carries the step's initial image (index 0) so the host knows it
   // on entry; subsequent scrubs come through notifyImageChange.
   useEffect(() => {
      if (!targetOrigin) {
         return;
      }
      const guide = guideRef.current;
      const image = imageInfoFor(guide, stepNumber, 0);
      lastImageKeyRef.current = imageKey(stepNumber, image.imageIndex);
      post({
         type: 'stepChange',
         guideid: guide.guideid,
         stepNumber,
         stepTitle: stepTitleFor(guide, stepNumber),
         stepText: stepTextFor(guide, stepNumber),
         ...image,
      });
   }, [stepNumber, targetOrigin, post]);

   const notifyImageChange = useCallback(
      (step: number, imageIndex: number) => {
         if (!targetOrigin) {
            return;
         }
         const guide = guideRef.current;
         const image = imageInfoFor(guide, step, imageIndex);
         const key = imageKey(step, image.imageIndex);
         if (key === lastImageKeyRef.current) {
            return;
         }
         lastImageKeyRef.current = key;
         post({ type: 'imageChange', guideid: guide.guideid, stepNumber: step, ...image });
      },
      [targetOrigin, post]
   );

   // Whether a trusted host frame is present. The full embed uses this to show
   // the active-step highlight only under a real host (e.g. FixBot): standalone,
   // there is no "current step" concept driving anything to highlight.
   const isBridged = targetOrigin !== null;

   return { notifyImageChange, isBridged };
}

// The step the viewer is on, or null for title (-1), intro (0), or conclusion
// (N+1), which aren't guide steps.
function stepForNumber(guide: Guide, stepNumber: number): Step | null {
   if (!Number.isInteger(stepNumber) || stepNumber < 1) {
      return null;
   }
   return guide.steps[stepNumber - 1] ?? null;
}

function stepTitleFor(guide: Guide, stepNumber: number): string | null {
   const step = stepForNumber(guide, stepNumber);
   const title = step?.title ? step.title.trim() : '';
   return title || null;
}

// Plain-text rendering of the current step for the host's model context, built
// from raw line text (human-readable wiki markup) and capped to a small payload.
function stepTextFor(guide: Guide, stepNumber: number): string | null {
   const step = stepForNumber(guide, stepNumber);
   if (!step || !Array.isArray(step.lines)) {
      return null;
   }
   const text = step.lines
      .map(line => (line?.text_raw ? line.text_raw.trim() : ''))
      .filter(Boolean)
      .join('\n')
      .trim();
   if (!text) {
      return null;
   }
   return text.length > STEP_TEXT_MAX_LENGTH
      ? `${text.slice(0, STEP_TEXT_MAX_LENGTH).trimEnd()}…`
      : text;
}

function imageKey(stepNumber: number, imageIndex: number | null): string {
   return `${stepNumber}:${imageIndex}`;
}

// The image the viewer is looking at within a step, for the host to attach as a
// vision input. Only image steps have a gallery; video/embed/media-less steps
// report none. imageIndex is clamped into the step's image array.
function imageInfoFor(guide: Guide, stepNumber: number, imageIndex: number): ImageInfo {
   const noImage: ImageInfo = { imageIndex: null, imageUrl: null, imageCount: 0 };
   const step = stepForNumber(guide, stepNumber);
   if (!step) {
      return noImage;
   }
   // Chaptered video-guide steps render the shared player, not their gallery.
   if (guide.isVideoGuide && step.videoTimestamp) {
      return noImage;
   }
   const media = step.media;
   const data = media?.data;
   if (!media || media.type !== 'image' || !Array.isArray(data) || data.length === 0) {
      return noImage;
   }
   const count = data.length;
   const index =
      Number.isInteger(imageIndex) && imageIndex >= 0 && imageIndex < count ? imageIndex : 0;
   return {
      imageIndex: index,
      imageUrl: imageUrlFrom(data[index] as StepImage),
      imageCount: count,
   };
}

// A URL the model can actually see: prefer large (800px), fall back through
// bigger/smaller pre-generated sizes to `original` (always present). Never a thumbnail.
function imageUrlFrom(image: StepImage | undefined): string | null {
   if (!image) {
      return null;
   }
   return (
      (image.large as string) ||
      (image.huge as string) ||
      (image.medium as string) ||
      (image.standard as string) ||
      (image.original as string) ||
      null
   );
}
